/**
 * Live Ball multiplayer service. Mirrors the parsing pipeline in
 *   packages/amgdragontiger_v2/lib/data/repositories/dragontiger_multiplayer_service.dart
 * AND its consumer at
 *   packages/amgdragontiger_v2/lib/presentation/bloc/game_bloc.dart
 *
 * SignalR delivers each event as positional args. The server normally sends
 * **two** args:
 *
 *   1. A status flag string — `"Success"` or `"Fail"`
 *   2. A JSON string that decodes to an envelope of the shape:
 *
 *     {
 *       Index: number,           // sequence #, used for de-dup
 *       Flag: 'Success' | 'Fail',
 *       State: string,           // server's own event name (mirrors arg #1's status)
 *       Data: any,               // event-specific payload (object | array | null)
 *       Content: string,         // human-readable description / error message
 *       TimeStamp: string,       // ISO timestamp
 *       …event-specific fields:
 *         StartTime / EndTime    // on StartBet, Result(restart)
 *         Result                 // on StartPlay
 *     }
 *
 * For `GetMessages` the server sends a single arg containing an **array of
 * envelopes** — the replay buffer. We expand that into one event per envelope,
 * keeping the original SignalR event name (matches dragontiger reference).
 *
 * The orchestrator subscribes once and receives clean `{name, envelope}`
 * objects; it never sees the raw flag string or JSON-encoded layer.
 */
import { SocketGameHub } from './hub';
import { warn } from '../util/logger';

export type MultiplayerEventName =
  | 'PlayerJoined'
  | 'PlayerLeft'
  | 'StartGame'
  | 'StartBet'
  | 'EndBet'
  | 'StartPlay'
  | 'Result'
  | 'PlaceBet'
  | 'GetMessages'
  | '_connection';

export interface MpEnvelope extends Record<string, unknown> {
  Index?: number;
  Flag?: 'Success' | 'Fail' | string;
  State?: string;
  Data?: unknown;
  Content?: string;
  TimeStamp?: string;
  StartTime?: string;
  EndTime?: string;
  Result?: string;
}

export interface MultiplayerEvent {
  name: MultiplayerEventName;
  /** Decoded envelope (after stripping the outer flag string + JSON-parsing). */
  envelope: MpEnvelope | null;
  /** Raw payload kept on `_connection` events so the orchestrator can read state strings. */
  raw?: unknown;
}

const REGISTERED_EVENTS: readonly Exclude<MultiplayerEventName, '_connection'>[] = [
  'PlayerJoined',
  'PlayerLeft',
  'StartGame',
  'StartBet',
  'EndBet',
  'StartPlay',
  'Result',
  'PlaceBet',
  'GetMessages',
] as const;

type Listener = (evt: MultiplayerEvent) => void;

export class MultiplayerService {
  private hub: SocketGameHub | null = null;
  private listeners: Set<Listener> = new Set();
  private connected = false;
  private lastSessionId: string | null = null;
  private lastGameKey: string | null = null;

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get isConnected(): boolean {
    return this.hub !== null && this.connected;
  }

  async connect(hubUrl: string): Promise<void> {
    if (this.hub) return;
    this.hub = new SocketGameHub(hubUrl, {
      onClose: (err) => {
        this.connected = false;
        this.emitConnection('closed', err?.message);
      },
      onReconnecting: (err) => {
        this.connected = false;
        this.emitConnection('reconnecting', err?.message);
      },
      onReconnected: (id) => {
        this.connected = true;
        this.emitConnection('reconnected', undefined, id);
        void this.resumeAfterReconnect();
      },
    });

    for (const evtName of REGISTERED_EVENTS) {
      this.hub.registerOnServerEvents(evtName, (args) => {
        for (const env of expandMessages(args)) {
          this.emit({ name: evtName, envelope: env });
        }
      });
    }

    await this.hub.startConnection(() => {
      this.connected = true;
      this.emitConnection('connected');
    });
  }

  async disconnect(): Promise<void> {
    const h = this.hub;
    this.hub = null;
    this.connected = false;
    if (h) await h.stopConnection();
  }

  async joinGame(sessionId: string, gameKey: string): Promise<void> {
    if (!this.hub) return;
    this.lastSessionId = sessionId;
    this.lastGameKey = gameKey;
    await this.hub.sendMessage('JoinGame', [sessionId, gameKey]);
  }

  async getMessages(): Promise<void> {
    if (!this.hub) return;
    await this.hub.sendMessage('GetMessages', []);
  }

  async placeBet(betItemId: number, amount: number): Promise<void> {
    if (!this.hub) return;
    await this.hub.sendMessage('PlaceBet', [betItemId, amount]);
  }

  private emit(evt: MultiplayerEvent): void {
    for (const l of this.listeners) {
      try { l(evt); } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[MultiplayerService listener]', e);
      }
    }
  }

  private emitConnection(state: string, error?: string, connectionId?: string): void {
    this.emit({
      name: '_connection',
      envelope: null,
      raw: { state, error, connectionId },
    });
  }

  private async resumeAfterReconnect(): Promise<void> {
    if (!this.lastSessionId || !this.lastGameKey) return;
    try {
      await this.joinGame(this.lastSessionId, this.lastGameKey);
      await this.getMessages();
    } catch (e) {
      warn('[MultiplayerService] resume failed', e);
    }
  }
}

// ====================================================================
// Envelope extraction (mirrors dragontiger's _extractMultiplayerEnvelope
// + _expandMultiplayerMessages + _looksLikeEnvelope)
// ====================================================================

const ENVELOPE_KEYS = ['Flag', 'Index', 'Data', 'Content', 'State'] as const;

function looksLikeEnvelope(v: unknown): v is MpEnvelope {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return ENVELOPE_KEYS.some((k) => k in o);
}

/** Try to decode a JSON string into an envelope. Returns null on failure. */
function tryParseJson(s: string): unknown {
  try { return JSON.parse(s); } catch { return null; }
}

/**
 * Walk an arbitrary payload structure and pull out the envelope object(s).
 * Cases handled:
 *   - args = []                           → no envelopes
 *   - args = [<envelope-obj>]             → 1 envelope
 *   - args = ["Success"|"Fail", "<json>"] → 1 envelope (json-decoded)
 *   - args = ["Success", <envelope-obj>]  → 1 envelope
 *   - args = [[<env>, <env>, …]]          → N envelopes (GetMessages replay)
 *   - args = ["<json-of-envelope>"]       → 1 envelope
 *   - args = ["<json-of-array>"]          → N envelopes
 *
 * Sorted by Index when present so replay events fire in order.
 */
function expandMessages(args: unknown[]): MpEnvelope[] {
  const collected: MpEnvelope[] = [];

  const visit = (v: unknown): void => {
    if (v == null) return;
    if (typeof v === 'string') {
      const decoded = tryParseJson(v);
      if (decoded !== null) visit(decoded);
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) visit(item);
      return;
    }
    if (looksLikeEnvelope(v)) {
      collected.push(v);
    }
  };

  for (const a of args) visit(a);

  collected.sort((a, b) => {
    const ai = typeof a.Index === 'number' ? a.Index : Number.MAX_SAFE_INTEGER;
    const bi = typeof b.Index === 'number' ? b.Index : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
  return collected;
}

// Exposed for tests / orchestrator inspection.
export const __mpInternals = { expandMessages, looksLikeEnvelope };
